import { Ellipsis, ExternalLink, RefreshCw, Vault } from 'lucide-react';
import { useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import type { SecretMetadata, SecretsOverview } from '../api/types';
import { ActionButton } from '../components/ui';
import { formatDateTime, formatRelative } from '../lib/format';
import { MenuButton, type MenuEntry } from '../shell/Menu';
import { strings } from '../strings';
import { SourceChip, StatusBadgeView, ValueCellView } from './cells';
import { TagsList } from './DetailsPane';
import { isGridKey, moveInGrid, type GridPosition } from './grid';
import { secretStatuses, secretValueCell, type ValueContext } from './model';
import { locationEntry, metadataWords } from './registry';

const s = strings.variables;

function TagsPopover({ secret }: { secret: SecretMetadata }) {
  const count = Object.keys(secret.tags).length;
  if (count === 0) return <span className="text-fg-faint">{strings.common.dash}</span>;
  return (
    <MenuButton
      portal
      buttonClassName="btn btn-ghost var-tags-button"
      buttonContent={metadataWords(s.secretsView.tagsButton(count), secret.location)}
      buttonLabel={metadataWords(s.secretsView.tagsOf(secret.name), secret.location)}
      buttonProps={{ tabIndex: -1 }}
      menuLabel={metadataWords(s.secretsView.tagsOf(secret.name), secret.location)}
      entries={[{ kind: 'note', id: 'tags', label: <TagsList tags={secret.tags} /> }]}
    />
  );
}

export interface SecretActionsFor {
  primary(secret: SecretMetadata): ReactNode;
  menu(secret: SecretMetadata): MenuEntry[];
}

/**
 * The Secrets view (spec 6.10.13): the vault strip, the summary banner and one row per secret in the source.
 * The design scope's declarations are edited in `DeclarationsEditor`.
 */
export function SecretsView({
  deploymentName,
  overview,
  visible,
  stripAction,
  valueContext,
  selected,
  highlighted,
  narrow,
  refreshing,
  canSelect,
  actions,
  onRefresh,
  onPrepare,
  prepareDisabled,
  onSelect,
  onSelectAll,
  onClearSelection,
  onHighlight,
  onDetails,
  onCopyValue,
  usedByChip,
}: {
  deploymentName: string | null;
  overview: SecretsOverview | null;
  /** The secrets to list, after search and filters. */
  visible: readonly SecretMetadata[];
  /** Extra control in the vault strip (the designer's Edit declarations). */
  stripAction?: ReactNode;
  valueContext: ValueContext;
  selected: ReadonlySet<string>;
  highlighted: string | null;
  narrow: boolean;
  refreshing: boolean;
  canSelect: boolean;
  actions: SecretActionsFor;
  onRefresh: () => void;
  onPrepare: () => void;
  prepareDisabled: string | null;
  onSelect: (name: string, mode: 'toggle' | 'range') => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onHighlight: (name: string | null) => void;
  onDetails: (name: string) => void;
  /** Ctrl+C on a value that can be revealed (spec 6.10.18); false when it can't be copied. */
  onCopyValue?: (secret: SecretMetadata) => boolean;
  usedByChip: (resourceId: string, settings: string[]) => ReactNode;
}) {
  const [focus, setFocus] = useState<GridPosition>({ row: 0, col: 0 });
  const [moved, setMoved] = useState(0);
  const body = useRef<HTMLTableSectionElement>(null);

  useLayoutEffect(() => {
    if (moved) body.current?.querySelector<HTMLElement>(`[data-cell="${focus.row}:${focus.col}"]`)?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moved]);

  if (!overview) return null;
  const { vault, summary } = overview;
  const ready = vault.status === 'ready';
  const columns = [
    ...(canSelect ? ['select'] : []),
    'name',
    'usedBy',
    'value',
    ...(narrow ? [] : ['version', 'lastChanged', 'expires', 'tags']),
    'status',
    'actions',
  ];
  const onKeyDown = (event: KeyboardEvent<HTMLTableSectionElement>) => {
    if (event.defaultPrevented) return;
    const cell = (event.target as HTMLElement).closest<HTMLElement>('[data-cell]');
    if (!cell || event.target !== cell) return;
    const [r, c] = cell.dataset.cell!.split(':').map(Number);
    if (isGridKey(event)) event.stopPropagation();
    const next = moveInGrid({ row: r, col: c }, event, {
      rows: visible.length,
      cellsIn: () => columns.length,
    });
    if (next && !event.shiftKey && !event.altKey) {
      event.preventDefault();
      setFocus(next.position);
      setMoved((n) => n + 1);
      return;
    }
    const secret = visible[r];
    const ctrl = event.ctrlKey || event.metaKey;
    if (!secret) return;
    if (event.key === ' ' && canSelect) {
      event.preventDefault();
      onSelect(secret.name, event.shiftKey ? 'range' : 'toggle');
    } else if (event.key.toLowerCase() === 'a' && ctrl && canSelect) {
      event.preventDefault();
      onSelectAll();
    } else if (event.key === 'Escape' && selected.size) {
      event.preventDefault();
      event.stopPropagation();
      onClearSelection();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      // The value cell's reveal controls (spec 6.10.18): Enter reveals, Ctrl+Enter expands.
      const control =
        columns[c] === 'value'
          ? cell.querySelector<HTMLButtonElement>(
              ctrl ? '[data-reveal-expand]' : '[data-reveal-button]:not([aria-disabled="true"])',
            )
          : null;
      if (event.altKey) onDetails(secret.name);
      else if (control) control.click();
      else onHighlight(highlighted === secret.name ? null : secret.name);
    } else if ((event.key === 'F10' && event.shiftKey) || event.key === 'ContextMenu') {
      event.preventDefault();
      document
        .querySelector<HTMLButtonElement>(`[data-row-menu="secret:${CSS.escape(secret.name)}"]`)
        ?.click();
    } else if (event.key.toLowerCase() === 'c' && ctrl) {
      event.preventDefault();
      if (columns[c] === 'value' && onCopyValue?.(secret)) return;
      void navigator.clipboard?.writeText(secret.name).catch(() => {});
    }
  };
  const cellProps = (row: number, col: number) => ({
    'data-cell': `${row}:${col}`,
    tabIndex: focus.row === row && focus.col === col ? 0 : -1,
    onFocus: () => setFocus({ row, col }),
  });
  const vaultLabel = vault.label ?? s.secretsView.vault;
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="var-vault-strip" data-testid="vault-strip">
        <span className="var-icon-tile var-tone-info" aria-hidden>
          <Vault className="size-3.5" />
        </span>
        <span className="font-semibold">{vaultLabel}</span>
        {vault.name && <span className="font-mono">{vault.name}</span>}
        {vault.status === 'ready' && (
          <span data-testid="vault-status">
            <StatusBadgeView
              status={{ id: 'resolved', tone: 'success', label: strings.vault.ready, help: '' }}
            />
          </span>
        )}
        {vault.status === 'not-created' && (
          <>
            <span data-testid="vault-status">
              <StatusBadgeView
                status={{ id: 'not-applied', tone: 'warning', label: strings.vault.notCreated, help: '' }}
              />
            </span>
            <ActionButton
              permission="operate"
              variant="primary"
              disabledReason={prepareDisabled}
              icon={<Vault aria-hidden className="size-4" />}
              onClick={onPrepare}
            >
              {s.actions.prepareVault}
            </ActionButton>
          </>
        )}
        {vault.region && vault.status !== 'none' && (
          <span className="text-fg-muted">{s.secretsView.placement(vault.region)}</span>
        )}
        <span className="ml-auto flex items-center gap-2">
          {stripAction}
          {vault.listedAt && (
            <span className="text-fg-muted" title={formatDateTime(vault.listedAt)}>
              {s.secretsView.listed(formatRelative(vault.listedAt))}
            </span>
          )}
          {ready && (
            <button
              type="button"
              className="btn btn-ghost btn-icon"
              aria-label={s.toolbar.refresh}
              title={s.toolbar.refresh}
              disabled={refreshing}
              onClick={onRefresh}
            >
              <RefreshCw aria-hidden className="size-4" />
            </button>
          )}
          {vault.consoleUrl && (
            <a
              className="link inline-flex items-center gap-1"
              href={vault.consoleUrl}
              target="_blank"
              rel="noreferrer"
            >
              {s.secretsView.console}
              <ExternalLink aria-hidden className="size-3" />
            </a>
          )}
        </span>
      </div>
      {vault.status === 'store' && (
        <p className="var-strip tone-warning" data-testid="vault-mode">
          {s.strips.store(deploymentName ?? '', vault.store?.name ?? '')}
        </p>
      )}
      {vault.status === 'none' && (
        <p className="var-strip tone-neutral" data-testid="vault-mode">
          {s.empty.noSecrets}
        </p>
      )}
      {(summary.missingValues > 0 || summary.changedOutside > 0 || summary.unmanaged > 0) && (
        <p role="status" data-testid="secrets-banner" className="var-strip tone-warning">
          {[
            summary.missingValues > 0 && strings.secrets.banner.missing(summary.missingValues),
            summary.changedOutside > 0 && strings.secrets.banner.outside(summary.changedOutside),
            summary.unmanaged > 0 && strings.secrets.banner.unmanaged(summary.unmanaged),
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      )}
      <div className="min-h-0 flex-1 overflow-auto">
        {visible.length === 0 ? (
          <p className="var-empty">{overview.secrets.length === 0 ? strings.secrets.none : s.table.noRows}</p>
        ) : (
          <table
            role="grid"
            aria-label={s.table.secretsLabel}
            aria-multiselectable="true"
            className="table var-table"
          >
            <thead>
              <tr role="row">
                {columns.map((column) => (
                  <th key={column} role="columnheader" scope="col" className={`var-th var-col-${column}`}>
                    {column === 'select' ? (
                      <input
                        type="checkbox"
                        tabIndex={-1}
                        aria-label={s.table.selectAll}
                        checked={selected.size > 0 && visible.every((x) => selected.has(x.name))}
                        onChange={() => (selected.size ? onClearSelection() : onSelectAll())}
                      />
                    ) : column === 'actions' ? (
                      <span className="sr-only">{s.columns.actions}</span>
                    ) : (
                      {
                        name: s.columns.secretInSource,
                        usedBy: s.columns.usedBy,
                        value: s.columns.valueShort,
                        version: s.columns.version,
                        lastChanged: s.columns.lastChanged,
                        expires: s.columns.expires,
                        tags: metadataWords(
                          s.columns.tags,
                          overview?.vault.type ? { type: overview.vault.type } : null,
                        ),
                        status: s.columns.status,
                      }[column]
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody ref={body} onKeyDown={onKeyDown}>
              {visible.map((secret, r) => {
                const location = secret.location;
                const cell = location
                  ? secretValueCell(secret, location, secret.kind, valueContext)
                  : ({ kind: 'unknown', label: s.value.label.unknown } as const);
                const resources = [...new Set(secret.usedBy.map((u) => u.resourceId))];
                const content = (column: string): ReactNode => {
                  switch (column) {
                    case 'select':
                      return (
                        <input
                          type="checkbox"
                          tabIndex={-1}
                          aria-label={s.table.selectRow(secret.name)}
                          checked={selected.has(secret.name)}
                          onChange={() => onSelect(secret.name, 'toggle')}
                          onClick={(event) => {
                            if (event.shiftKey) {
                              event.preventDefault();
                              onSelect(secret.name, 'range');
                            }
                          }}
                        />
                      );
                    case 'name':
                      return (
                        <span className="flex min-w-0 flex-col">
                          <span className="flex min-w-0 items-center gap-1.5">
                            <button
                              type="button"
                              tabIndex={-1}
                              className="link truncate font-mono font-semibold"
                              aria-pressed={highlighted === secret.name}
                              title={s.secretsView.selectSecret(secret.name)}
                              onClick={() => onHighlight(highlighted === secret.name ? null : secret.name)}
                            >
                              {secret.name}
                            </button>
                            <span className="var-kind">{s.kinds[secret.kind]}</span>
                            {secret.generated && (
                              <span className="badge tone-info">{s.secretsView.generated}</span>
                            )}
                            {secret.ignored && (
                              <span className="badge tone-neutral">{s.secretsView.ignored}</span>
                            )}
                          </span>
                          {secret.description && (
                            <span className="truncate text-[11.5px] text-fg-muted">{secret.description}</span>
                          )}
                          {narrow && location && <SourceChip location={location} compact />}
                        </span>
                      );
                    case 'usedBy':
                      return resources.length ? (
                        <span className="flex flex-wrap gap-1">
                          {resources.map((id) =>
                            usedByChip(
                              id,
                              secret.usedBy.filter((u) => u.resourceId === id).map((u) => u.setting),
                            ),
                          )}
                        </span>
                      ) : (
                        <span className="text-fg-faint">{strings.common.dash}</span>
                      );
                    case 'value':
                      return <ValueCellView cell={cell} />;
                    case 'version':
                      return (
                        <span className="font-mono text-[12px]">
                          {secret.currentVersion && location
                            ? locationEntry(location).formatVersion(secret.currentVersion)
                            : strings.common.dash}
                        </span>
                      );
                    case 'lastChanged':
                      return (
                        <span className="text-[12px] text-fg-muted" title={formatDateTime(secret.updatedAt)}>
                          {secret.updatedAt
                            ? `${formatRelative(secret.updatedAt)} ${secret.updatedBy ? strings.common.by(secret.updatedBy) : `· ${s.value.outsideNebisys}`}`
                            : strings.common.dash}
                        </span>
                      );
                    case 'expires':
                      return (
                        <span className="text-[12px]">
                          {secret.expiresAt ? formatDateTime(secret.expiresAt) : strings.common.dash}
                        </span>
                      );
                    case 'tags':
                      return <TagsPopover secret={secret} />;
                    case 'status': {
                      const statuses = secretStatuses(secret, valueContext.listingFailed);
                      return (
                        <span className="flex flex-wrap items-center gap-1">
                          {statuses.map((status) => (
                            <StatusBadgeView key={status.label} status={status} />
                          ))}
                        </span>
                      );
                    }
                    case 'actions':
                      return (
                        <span className="flex items-center justify-end gap-1">
                          {actions.primary(secret)}
                          <MenuButton
                            portal
                            placement="below-end"
                            buttonClassName="btn btn-ghost btn-icon var-row-menu"
                            buttonLabel={s.table.rowMenu(secret.name)}
                            buttonTitle={s.table.rowMenu(secret.name)}
                            buttonContent={<Ellipsis aria-hidden className="size-4" />}
                            buttonProps={{ tabIndex: -1, 'data-row-menu': `secret:${secret.name}` }}
                            menuLabel={s.table.rowMenu(secret.name)}
                            entries={actions.menu(secret)}
                          />
                        </span>
                      );
                  }
                };
                return (
                  <tr
                    key={secret.name}
                    role="row"
                    aria-selected={selected.has(secret.name)}
                    data-testid={`secret-${secret.name}`}
                    className={`var-row ${selected.has(secret.name) ? 'is-selected' : ''} ${highlighted === secret.name ? 'is-highlighted' : ''}`}
                  >
                    {columns.map((column, c) => (
                      <td
                        key={column}
                        role="gridcell"
                        className={`var-td var-col-${column}`}
                        {...cellProps(r, c)}
                      >
                        {content(column)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
